'use client';

import type { FC, ReactNode } from 'react';
import { useLayoutEffect } from 'react';
import { useScreenLayout } from './useScreenLayout';

interface ScreenFixedBottomProps {
  children: ReactNode;
  className?: string;
  /** Override the auto-gate. Left undefined the scrim follows whether there are
   * children; set it explicitly only when content is present but must NOT draw a
   * scrim (e.g. an `invisible` wrapper during a spin), or vice versa. */
  showBlur?: boolean;
}

export const ScreenFixedBottom: FC<ScreenFixedBottomProps> = ({ children, className, showBlur }) => {
  const { setFixedBottom, clearFixedBottom } = useScreenLayout();

  // Auto-gate: no children → no scrim. So a bar that renders nothing this frame
  // (e.g. its button is hidden mid-roll) can't leave an empty blur behind. Pass a
  // `null`/`false` child when there's nothing to show rather than an empty wrapper.
  const blur = showBlur ?? (children != null && children !== false);

  // update content on every change — NO cleanup here, so a content swap doesn't
  // momentarily clear the bottom (which collapsed its height var and jumped scroll)
  useLayoutEffect(() => {
    setFixedBottom(children, className, blur);
  }, [children, className, blur, setFixedBottom]);

  // clear only on real unmount
  useLayoutEffect(() => () => clearFixedBottom(), [clearFixedBottom]);

  return null;
};
